import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  input,
  InputSignal,
  InputSignalWithTransform,
  OnDestroy,
  OnInit,
  output,
  Signal,
  signal,
  viewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActiveFilters, GridColumn } from '@grid/abstractions';
import { parseToBoolean } from '@shared/functions/parseToBoolean';
import { ColumnFilterState } from '@shared/modules/prime-grid-filters/models/column-filter-state';
import { ColumnFilterOperatorService } from '@shared/services/column-filter-operator.service';
import { PlainObject } from 'odata-query';
import { Subject } from 'rxjs';
import { parseToFilterGridColumn } from '../../helpers/functions/parse-to-filter-grid-column';
import { FilterMap } from '../../helpers/types/filter-map.type';
import { ColumnFilterDefinition } from '../../models/column-filter-definition';
import { FilterSessionManagerService } from '../../services/filter-session-manager.service';
import { FilterSessionService } from '../../services/filter-session.service';
import { InitialFilterLoaderService } from '../../services/initial-filter-loader.service';
import { PrimeGridFilterToolbarComponent } from '../prime-grid-filter-toolbar/prime-grid-filter-toolbar.component';

@Component({
  standalone: false,
  selector: 'gstar-prime-grid-filter-form',
  templateUrl: './prime-grid-filter-form.component.html',
  styleUrl: './prime-grid-filter-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrimeGridFilterFormComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private readonly initialFilterLoader = inject(InitialFilterLoaderService);
  private readonly filterSession = inject(FilterSessionService);
  private readonly filterSessionManager = inject(FilterSessionManagerService);
  private readonly columnFilterOperatorService = inject(
    ColumnFilterOperatorService
  );
  private readonly destroyRef = inject(DestroyRef);

  triggerByChanges = input(false, { transform: parseToBoolean });
  filters: InputSignalWithTransform<ColumnFilterDefinition[], GridColumn[]> =
    input([], {
      transform: parseToFilterGridColumn,
    });
  sessionKey: InputSignal<string | undefined> = input();
  queryFilter: WritableSignal<FilterMap> = signal(new Map());
  hasFilters: WritableSignal<boolean> = signal(false);
  willRemoveFilters: ColumnFilterState<unknown>[] = [];

  public destroy: Subject<void> = new Subject();

  toolbar: Signal<PrimeGridFilterToolbarComponent | undefined> =
    viewChild('toolbar');

  queryChange = output<PlainObject>();
  clearAll = output<void>();

  constructor() {
    this.columnFilterOperatorService.run
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const queryFilter: FilterMap = this.queryFilter();
        this.queryChange.emit(queryFilter);
      });

    this.initialFilterLoader.allFiltersLoaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.hasFilters.set(this.queryFilter().size > 0);
      });
  }

  ngOnInit(): void {
    this.initialFilterLoader.startSession(this.filters(), this.sessionKey());
  }

  ngAfterViewInit(): void {
    if (this.filterSession.hasStoredSession) {
      this.toolbar()!.isCollapse.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    if (this.sessionKey()) {
      this.filterSession.destroySession();
    }
  }

  getActiveFilters(): ActiveFilters {
    return this.columnFilterOperatorService.filters;
  }

  filterHandler(filterColumn: ColumnFilterState<unknown>): void {
    const filters: FilterMap = this.queryFilter();

    if (filterColumn.hasValue()) {
      if (filterColumn.columnQueryBuilder) {
        filterColumn.columnQueryBuilder(filterColumn, filters);
        filters.set(filterColumn.columnName, filterColumn);
        if (this.triggerByChanges() && this.sessionKey()) {
          this.filterSessionManager.updateFilter(filterColumn);
        }
      }
    } else if (filters.has(filterColumn.columnName)) {
      filters.delete(filterColumn.columnName);

      if (this.triggerByChanges() && this.sessionKey()) {
        this.filterSessionManager.removeFilter(filterColumn);
      } else {
        this.willRemoveFilters.push(filterColumn);
      }
    }

    this.columnFilterOperatorService.updateActiveFilters(filters);
    this.columnFilterOperatorService.checkStatus.next();

    if (this.triggerByChanges() && !filterColumn.assignWithoutTrigger) {
      this.applyFilter();
    } else {
      filterColumn.assignWithoutTrigger = false;
    }
  }

  applyFilter(): void {
    const queryFilter: Map<string, PlainObject> = new Map();
    const filtersIterable = this.queryFilter().values();
    let iterator = filtersIterable.next();

    while (!iterator.done) {
      if (queryFilter.has(iterator.value.path)) {
        const storedQuery: PlainObject = queryFilter.get(iterator.value.path)!;
        const storedQueryArray: PlainObject[] = storedQuery['and']
          ? storedQuery['and']
          : [storedQuery];
        const duplicatedQuery = iterator.value.getQuery();
        queryFilter.set(iterator.value.path, {
          and: [...storedQueryArray, duplicatedQuery],
        });
      } else {
        queryFilter.set(iterator.value.path, iterator.value.getQuery());
      }

      if (!this.triggerByChanges() && this.sessionKey()) {
        this.filterSessionManager.updateFilter(iterator.value);
      }

      iterator = filtersIterable.next();
    }

    if (this.sessionKey()) {
      this.filterSessionManager.updateSkip(0);
    }

    if (
      !this.triggerByChanges() &&
      this.willRemoveFilters.length > 0 &&
      this.sessionKey()
    ) {
      this.willRemoveFilters.forEach(
        (filter: ColumnFilterState<unknown>): void => {
          this.filterSessionManager.removeFilter(filter);
        }
      );
    }

    this.hasFilters.set(queryFilter.size > 0);
    const query = Object.assign({}, ...queryFilter.values());
    this.queryChange.emit(query);
  }

  clearAndRun(): void {
    this.clearFields();
    this.queryChange.emit({});
  }

  clearFields(): void {
    const queryFilter: FilterMap = this.queryFilter();
    queryFilter.clear();
    this.hasFilters.set(false);
    this.queryFilter.set(queryFilter);
    this.columnFilterOperatorService.clearAll.next();
    this.clearAll.emit();
  }
}
